import {
  Add16Regular,
  Apps16Regular,
  ArrowClockwise16Regular,
  ArrowRight16Regular,
  Board16Regular,
  CalendarLtr16Regular,
  ChartMultiple16Regular,
  CheckboxChecked16Regular,
  Color16Regular,
  Diamond16Regular,
  Document16Regular,
  Eye16Regular,
  Folder16Regular,
  History16Regular,
  Home16Regular,
  Image16Regular,
  Link16Regular,
  Prompt16Regular,
  Sparkle16Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { isISODate, todayISO } from "@/engine/date";
import { diffSchedules, type ScheduleImpact } from "@/engine/diff";
import type { ExecutionStatus, Project, Task } from "@/engine/types";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/date-format";
import { fmtDelta, modKey } from "@/lib/format";
import { appGo } from "@/app/navigate";
import { activityPath } from "@/features/activity/activity-open";
import { planHref } from "@/shell/project-nav";
import { useSettingsHub } from "@/shell/settings-hub";
import { canCreateOwnTaskNow, usePlanSurface } from "@/features/access/use-plan-access";
import { paletteWorkspaceDocuments } from "@/features/docs/doc-index";
import { DOCUMENT_KIND_LABEL, documentCode, documentTitle } from "@/features/docs/doc-model";
import { useWorkspaceDocRows } from "@/features/docs/use-workspace-docs";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { collectFileEntries, fileOpenPath, fileSourceCaption, fileSourceTitle, type FileKind } from "@/features/files/file-index";
import { inspectPlan, planCover } from "@/features/plans/plan-gallery";
import { statusGlyph } from "@/features/tasks/task-glyphs";
import { useCatalogStore } from "@/store/catalog-store";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import * as ops from "@/store/tree-ops";
import { saveText } from "@/desktop/file-io";
import { isDesktop } from "@/desktop/runtime";

type CmdIcon = "home" | "plans" | "portfolio" | "tasks" | "files" | "design" | "add" | "board" | "reset" | "doc" | "calendar" | "command" | "view" | "check" | "history";

interface Cmd {
  id: string;
  label: string;
  detail?: string;
  hint?: string;
  keywords?: string;
  shortcut?: string;
  icon?: CmdIcon;
  group: "명령" | "Task" | "보기" | "이동";
  run: () => void;
  when?: () => boolean;
}

type PaletteScope = "all" | "plan" | "task" | "execution" | "doc" | "file" | "move" | "command";

const SCOPE_CHIPS: { id: PaletteScope; label: string }[] = [
  { id: "all", label: "전체" },
  { id: "plan", label: "플랜" },
  { id: "task", label: "작업" },
  { id: "execution", label: "Task" },
  { id: "doc", label: "문서" },
  { id: "file", label: "파일" },
  { id: "move", label: "이동" },
  { id: "command", label: "명령" },
];

type DotTone = "active" | "late" | "done" | "muted";

const itemClass = "gap-2.5 px-2 py-1.5";

function cmdGlyph(icon: CmdIcon) {
  switch (icon) {
    case "home":
      return <Home16Regular />;
    case "plans":
      return <Apps16Regular />;
    case "portfolio":
      return <ChartMultiple16Regular />;
    case "tasks":
      return <TaskListSquareLtr16Regular />;
    case "files":
      return <Folder16Regular />;
    case "design":
      return <Color16Regular />;
    case "add":
      return <Add16Regular />;
    case "board":
      return <Board16Regular />;
    case "reset":
      return <ArrowClockwise16Regular />;
    case "doc":
      return <Document16Regular />;
    case "history":
      return <History16Regular />;
    case "calendar":
      return <CalendarLtr16Regular />;
    case "view":
      return <Eye16Regular />;
    case "check":
      return <CheckboxChecked16Regular />;
    default:
      return <Prompt16Regular />;
  }
}

function iconFor(cmd: Cmd) {
  if (cmd.icon) return cmdGlyph(cmd.icon);
  if (cmd.group === "보기") return cmdGlyph("view");
  if (cmd.group === "Task") return cmdGlyph("check");
  if (cmd.group === "이동") return cmdGlyph("calendar");
  return cmdGlyph("command");
}

function fileGlyph(kind: FileKind) {
  if (kind === "image") return <Image16Regular />;
  if (kind === "link") return <Link16Regular />;
  return <Document16Regular />;
}

function IconWell({ children }: { children: ReactNode }) {
  return <span className="grid size-7 shrink-0 place-items-center rounded-md bg-foreground/[0.06] text-muted-foreground">{children}</span>;
}

function Hit({ icon, title, detail, detailClassName, meta, wrapDetail = false }: { icon: ReactNode; title: string; detail?: string; detailClassName?: string; meta?: ReactNode; wrapDetail?: boolean }) {
  return (
    <>
      <IconWell>{icon}</IconWell>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body text-[var(--text-strong)]" title={title}>
          {title}
        </span>
        {detail ? (
          <span className={cn("block text-caption text-muted-foreground", wrapDetail ? "whitespace-normal" : "truncate", detailClassName)} title={wrapDetail ? undefined : detail}>
            {detail}
          </span>
        ) : null}
      </span>
      {meta}
      <ArrowRight16Regular className="palette-go size-3.5 shrink-0 text-muted-foreground opacity-0" />
    </>
  );
}

function DotLabel({ tone, label }: { tone: DotTone; label: string }) {
  const quiet = tone === "done" || tone === "muted";
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 text-caption", quiet ? "text-muted-foreground" : "text-foreground")}>
      <span className={cn("size-1.5 rounded-full", tone === "late" ? "bg-critical" : tone === "active" ? "bg-tint" : "bg-muted-foreground", quiet && "opacity-50")} aria-hidden />
      {label}
    </span>
  );
}

function ExecutionMeta({ status }: { status: ExecutionStatus | undefined }) {
  const quiet = !status || status === "done";
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 text-caption [&_svg]:size-3", quiet ? "text-muted-foreground" : "text-foreground")}>
      {statusGlyph(status, 12, true)}
      {status ? STATUS_LABEL[status] : "상태 미정"}
    </span>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <CommandGroup heading={heading} className="px-1.5 py-1 [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:text-caption">
      {children}
    </CommandGroup>
  );
}

function tokensMatch(q: string, text: string): boolean {
  const hay = text.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => hay.includes(t));
}

/** "3.2" (WBS) or "#12" / "12" (row) → task */
function resolveRef(project: Project, ref: string): { task: Task; wbs: string } | null {
  const s = useProjectStore.getState();
  const r = ref.replace(/^#/, "");
  if (/^\d+$/.test(r) && !r.includes(".")) {
    const byRow = project.tasks[Number(r) - 1];
    // prefer WBS "3" (top-level) when it exists, else row number
    for (const [id, t] of s.derived.schedule.tasks) if (t.wbs === r) return { task: project.tasks.find((x) => x.id === id)!, wbs: t.wbs };
    if (byRow) return { task: byRow, wbs: s.derived.schedule.tasks.get(byRow.id)?.wbs ?? r };
    return null;
  }
  for (const [id, t] of s.derived.schedule.tasks) if (t.wbs === r) return { task: project.tasks.find((x) => x.id === id)!, wbs: t.wbs };
  return null;
}

interface QuickEdit {
  label: string;
  hint: string;
  impact: ScheduleImpact | null;
  error?: string;
  apply: () => void;
}

/** Parse "3.2 기간 8", "3.2 진척 50", "3.2 선행 2.6FS+2", "3.2 이름 …" and pre-compute the schedule impact. */
function parseQuickEdit(query: string): QuickEdit | null {
  const m = /^(#?\d+(?:\.\d+)*)\s+(기간|진척|선행|이름|마감)\s+(.+?)\s*\??$/.exec(query.trim());
  if (!m) return null;
  const s = useProjectStore.getState();
  const project = s.project;
  const found = resolveRef(project, m[1]!);
  if (!found) return { label: `${m[1]} — 해당 작업 없음`, hint: "작업은 WBS 번호(3.2) 또는 행 번호(#12)로 적습니다", impact: null, error: "no-task", apply: () => {} };
  const { task, wbs } = found;
  const field = m[2]!;
  const value = m[3]!.trim();
  const sched = s.derived.schedule.tasks.get(task.id)!;

  let next: Project | null = null;
  let label = "";
  let apply: () => void = () => {};
  if (field === "기간") {
    const n = Math.max(0, Math.round(Number(value.replace(/d$/i, ""))));
    if (!Number.isFinite(n) || sched.isSummary) return { label: `${wbs} ${task.name}: 기간 변경 불가`, hint: sched.isSummary ? "단계의 기간은 하위 작업에서 자동으로 정해집니다" : "숫자를 입력해 주세요", impact: null, error: "invalid", apply };
    next = { ...project, tasks: ops.updateTask(project.tasks, task.id, { duration: n }) };
    label = `${wbs} ${task.name}: 기간 ${task.duration}d → ${n}d`;
    apply = () => s.updateTask(task.id, { duration: n }, label);
  } else if (field === "진척") {
    const n = Math.max(0, Math.min(100, Math.round(Number(value.replace(/%$/, "")))));
    if (!Number.isFinite(n) || sched.isSummary) return { label: `${wbs} ${task.name}: 진척 변경 불가`, hint: "진척은 하위 작업이 없는 작업에만 넣을 수 있습니다", impact: null, error: "invalid", apply };
    next = { ...project, tasks: ops.updateTask(project.tasks, task.id, { percentComplete: n }) };
    label = `${wbs} ${task.name}: 진척 ${task.percentComplete}% → ${n}%`;
    apply = () => s.updateTask(task.id, { percentComplete: n }, label);
  } else if (field === "선행") {
    // accept WBS refs as well as row numbers
    const text = value
      .split(/[,;\s]+/)
      .filter(Boolean)
      .map((p) => {
        const mm = /^(#?\d+(?:\.\d+)*)((?:FS|SS|FF|SF)?[+-]?\d*)$/i.exec(p);
        if (!mm) return p;
        const r = resolveRef(project, mm[1]!);
        const row = r ? project.tasks.indexOf(r.task) + 1 : NaN;
        return Number.isFinite(row) ? `${row}${mm[2] ?? ""}` : p;
      })
      .join(", ");
    const parsed = ops.parsePredecessors(project, task.id, text);
    if (!parsed) return { label: `${wbs} ${task.name}: 선행 형식 오류`, hint: "예) 2.6 · 2.6FS+2 · 3.1SS · #7FF-1", impact: null, error: "invalid", apply };
    next = ops.releaseAutoConstraints({ ...project, dependencies: [...project.dependencies.filter((d) => d.successorId !== task.id), ...parsed.map((d) => ({ ...d, id: ops.newId("d") }))] });
    label = `${wbs} ${task.name}: 선행 → ${text || "없음"}`;
    apply = () => void s.setPredecessorsFromText(task.id, text);
  } else if (field === "이름") {
    label = `${wbs} 이름 → ${value}`;
    apply = () => s.updateTask(task.id, { name: value }, label);
    return { label, hint: "날짜는 바뀌지 않습니다", impact: null, apply };
  } else if (field === "마감") {
    const iso = parseDate(value);
    if (!iso) return { label: `${wbs} ${task.name}: 날짜 형식 오류`, hint: "예) 10/30 · 2026-10-30 · 해제", impact: null, error: "invalid", apply };
    next = { ...project, tasks: ops.updateTask(project.tasks, task.id, { deadline: iso === "clear" ? undefined : iso }) };
    label = `${wbs} ${task.name}: 마감 ${iso === "clear" ? "해제" : formatDate(iso, "numeric")}`;
    apply = () => s.updateTask(task.id, { deadline: iso === "clear" ? undefined : iso }, label);
  }
  if (!next) return null;
  const cal = new WorkCalendar(next.calendar);
  const after = computeSchedule(next, cal);
  if (after.errors.length) return { label, hint: after.errors[0]!.message, impact: null, error: "engine", apply };
  const impact = diffSchedules(next, s.derived.schedule, after, label, cal);
  const parts = [`종료일 ${fmtDelta(impact.finishDeltaDays)}`, `영향 ${impact.changedTaskIds.length}개`];
  if (impact.criticalPathChanged) parts.push("여유 없는 작업 달라짐");
  if (impact.newNegativeFloat.length) parts.push(`마감 못 지키는 작업 ${impact.newNegativeFloat.length}개`);
  return { label, hint: `미리보기 · ${parts.join(" · ")}`, impact, apply };
}

function parseDate(text: string): string | "clear" | null {
  const t = text.trim();
  if (/^(해제|없음|clear|none)$/i.test(t)) return "clear";
  if (isISODate(t)) return t;
  const now = todayISO();
  let m = /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/.exec(t);
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[-./](\d{1,2})$/.exec(t);
  if (m) return iso(Number(now.slice(0, 4)), Number(m[1]), Number(m[2]));
  return null;
}
function iso(y: number, mo: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
}

export function CommandPalette() {
  const open = useProjectStore((s) => s.dialog === "palette");
  const openDialog = useProjectStore((s) => s.openDialog);
  const surface = usePlanSurface();
  const workspaceRole = useWorkspaceRole();
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<PaletteScope>("all");
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) {
      setQuery("");
      setScope("all");
    }
  }

  const close = () => openDialog(null);
  const run = (fn: () => void) => {
    close();
    // let the dialog unmount before mutating focus-sensitive state
    requestAnimationFrame(fn);
  };

  const s = useProjectStore.getState;
  const catalogProjects = useCatalogStore((st) => st.projects);
  const commands: Cmd[] = useMemo(() => {
    const focus = () => s().focusId;
    const sel = () => s().selection;
    const editing = () => !!s().activeId;
    const pid = () => s().activeId;
    const items: Cmd[] = [
      { id: "home", group: "이동", icon: "home", label: "홈", detail: "오늘 할 일", keywords: "home 홈 시작 오늘", run: () => appGo("/home") },
      { id: "plans", group: "이동", icon: "plans", label: "플랜", detail: "플랜 목록", keywords: "plans 목록 프로젝트", run: () => appGo("/") },
      { id: "portfolio", group: "이동", icon: "portfolio", label: "포트폴리오", detail: "인력과 예산", keywords: "portfolio 포트폴리오 인력 mm 예산 비용 팀", run: () => appGo("/portfolio") },
      { id: "my-tasks", group: "이동", icon: "tasks", label: "내 작업", detail: "모든 플랜", keywords: "my tasks planner 담당 할일 모든", run: () => appGo("/tasks") },
      { id: "docs", group: "이동", icon: "doc", label: "문서", detail: "워크스페이스", keywords: "docs 문서 회의록 메모 자료 워크스페이스", run: () => appGo("/docs") },
      { id: "files", group: "이동", icon: "files", label: "파일", detail: "워크스페이스", keywords: "files 첨부 이미지 문서 워크스페이스", run: () => appGo("/files") },
      { id: "activity", group: "이동", icon: "history", label: "활동", detail: "워크스페이스", keywords: "activity 활동 변경 이력 로그", run: () => appGo("/activity") },
      { id: "design-system", group: "이동", icon: "design", label: "디자인 시스템", detail: "토큰과 컴포넌트", keywords: "tokens 버튼 아이콘 메타포", run: () => appGo("/design-system") },
      { id: "new-project", group: "명령", icon: "add", label: "새 플랜…", detail: "빈 플랜을 만듭니다", keywords: "create plan 생성", when: () => canWorkspace(workspaceRole, "plan.create"), run: () => openDialog("create-plan") },
      { id: "sample", group: "명령", icon: "board", label: "ERP 샘플 플랜 추가", detail: "예시 일정을 넣습니다", keywords: "sample", run: () => appGo(`/projects/${s().loadSample()}/schedule`) },
      {
        id: "demo-catalog",
        group: "명령",
        icon: "reset",
        label: "이 워크스페이스 목업 초기화",
        detail: "예시 데이터로 되돌립니다",
        keywords: "sample demo 예시 ERP 로봇 마케팅 워크스페이스",
        run: () => {
          s().loadDemoCatalog();
          appGo("/");
        },
      },
    ];
    items.push(
      { id: "add", group: "명령", label: "작업 추가", shortcut: `${modKey}↩`, when: () => editing() && surface.editWbs, run: () => s().addSibling(focus()) },
      { id: "add-child", group: "명령", label: "하위 작업 추가", shortcut: `⇧${modKey}↩`, when: () => !!focus() && surface.editWbs, run: () => s().addChild(focus()!) },
      { id: "add-ms", group: "명령", label: "마일스톤 추가", keywords: "milestone", when: () => surface.editWbs, run: () => s().addSibling(focus(), { duration: 0, name: "마일스톤" }) },
      { id: "indent", group: "명령", label: "들여쓰기", shortcut: "⇥", when: () => sel().length > 0 && surface.editWbs, run: () => s().indent(sel()) },
      { id: "outdent", group: "명령", label: "내어쓰기", shortcut: "⇧⇥", when: () => sel().length > 0 && surface.editWbs, run: () => s().outdent(sel()) },
      { id: "link", group: "명령", label: "선택 작업 순서대로 연결 (FS)", when: () => sel().length > 1 && surface.editWbs, run: () => s().linkSelection() },
      { id: "group", group: "명령", label: "선택 작업을 새 단계로 묶기", keywords: "요약 그룹", when: () => sel().length > 0 && surface.editWbs, run: () => s().groupSelection() },
      { id: "delete", group: "명령", label: "선택 작업 삭제", shortcut: `${modKey}⌫`, when: () => sel().length > 0 && surface.editWbs, run: () => s().requestDeleteTasks(sel()) },
      { id: "undo", group: "명령", label: "실행 취소", shortcut: `${modKey}Z`, when: () => s().past.length > 0 && surface.undo, run: () => s().undo() },
      { id: "redo", group: "명령", label: "다시 실행", shortcut: `⇧${modKey}Z`, when: () => s().future.length > 0 && surface.undo, run: () => s().redo() },
      { id: "baseline", group: "명령", label: "현재 일정을 기준선으로 저장", keywords: "baseline BL", when: () => surface.createBaseline, run: () => s().saveBaseline() },
      { id: "checkpoint", group: "명령", label: "체크포인트 저장", keywords: "snapshot 스냅샷", when: () => surface.editWbs || surface.editSchedule, run: () => s().saveCheckpoint() },
      { id: "report-close", group: "명령", label: "보고 기간 마감…", keywords: "report 리포트 주간보고 월간보고 마감 period close", when: () => !!pid() && surface.editSchedule, run: () => openDialog("report-close") },
      { id: "status", group: "명령", label: "진척 업데이트…", keywords: "상태 갱신 주간 status update", when: () => surface.editProgress, run: () => openDialog("status-update") },
      { id: "settings", group: "명령", label: "앱 설정", keywords: "settings 설정 보기 계정 모양", run: () => useSettingsHub.getState().open("app") },
      { id: "plan-settings", group: "명령", label: "플랜 설정", keywords: "plan settings 이름 멤버 공유 기간", when: () => !!pid(), run: () => useSettingsHub.getState().open("plan") },
      { id: "shortcuts", group: "명령", label: "단축키 보기", shortcut: `${modKey}/`, run: () => useSettingsHub.getState().open("app", "shortcuts") },
      { id: "export", group: "명령", label: "JSON 내보내기", when: () => surface.exportPlan, run: () => void saveText(`${s().project.name}.json`, s().exportProject()) },
      { id: "plan-files", group: "이동", icon: "files", label: "이 플랜의 파일", detail: "현재 플랜", keywords: "files 첨부 이미지 문서", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/files`) },
      { id: "go-docs", group: "이동", icon: "doc", label: "이 플랜의 문서", detail: "현재 플랜", keywords: "docs 문서 회의록 메모 자료", when: () => !!pid(), run: () => appGo(planHref(pid()!, "docs")) },
      { id: "go-activity", group: "이동", icon: "history", label: "이 플랜의 활동", detail: "현재 플랜", keywords: "activity 활동 변경 이력", when: () => !!pid(), run: () => appGo(activityPath(pid())) },
      { id: "system-logs", group: "이동", label: "로그 보기", keywords: "log logs 로그 기록 mcp 활동", shortcut: `${modKey}${modKey === "⌘" ? "⌥" : "Alt+"}L`, when: isDesktop, run: () => useSettingsHub.getState().open("app", "logs") },
      { id: "system-mcp", group: "이동", label: "MCP 연결", keywords: "mcp cursor claude 연결 연동 ai", shortcut: `${modKey}${modKey === "⌘" ? "⌥" : "Alt+"}M`, when: isDesktop, run: () => useSettingsHub.getState().open("app", "integration") },

      // Planner-style execution tasks: kept apart from the WBS-number commands above
      { id: "x-new", group: "Task", label: "새 Task…", shortcut: `${modKey}E`, keywords: "task todo planner 할일 추가", when: () => editing() && surface.editTask && canCreateOwnTaskNow(), run: () => s().addExecutionTask({}) },
      {
        id: "x-new-linked",
        group: "Task",
        label: "선택한 WBS·작업에 연결된 Task 만들기",
        keywords: "link 연결 범위",
        when: () => !!focus() && surface.editTask && canCreateOwnTaskNow(),
        run: () => {
          const id = focus()!;
          const st = s().derived.schedule.tasks.get(id);
          s().addExecutionTask({ linkedTaskIds: [id], start: st?.start, due: st?.finish });
        },
      },
      {
        id: "x-linked",
        group: "Task",
        label: "선택한 WBS·작업의 Task 보기",
        keywords: "linked 연결된 목록",
        when: () => !!focus() && !!pid(),
        run: () => appGo(`/projects/${pid()}/tasks?linked=${encodeURIComponent(focus()!)}`),
      },
      { id: "x-done", group: "Task", label: "열린 Task 완료 처리", keywords: "complete done 완료", when: () => !!s().executionFocusId && surface.editTask, run: () => s().setExecutionStatus(s().executionFocusId!, "done") },
      { id: "x-settings", group: "Task", label: "버킷·라벨", keywords: "bucket label 설정 버킷 라벨 작업 설정", when: () => editing() && surface.editWbs, run: () => useSettingsHub.getState().open("plan", "buckets") },
      { id: "go-tasks", group: "Task", label: "작업 탭", keywords: "tasks list 목록", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/tasks`) },
      { id: "go-board", group: "Task", label: "작업 보드 보기", keywords: "board kanban 칸반", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/tasks?view=board`) },
      { id: "go-calendar", group: "Task", label: "캘린더 탭", keywords: "calendar month week 월 주", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/calendar`) },
      { id: "go-analytics", group: "Task", label: "작업 분석 보기", keywords: "analytics charts 분석 차트 통계", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/tasks?view=analytics`) },
      { id: "go-reports", group: "Task", label: "현황 탭", keywords: "status overview 요약 report 리포트 보고서 변경 내역 추이 예측", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/reports`) },
      { id: "go-weekly", group: "Task", label: "주간 보고 탭", keywords: "weekly report 주간보고 주간 보고", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/weekly`) },
      { id: "go-forecast", group: "Task", label: "현황 예측 보기", keywords: "forecast 예측 남은 작업 크리티컬 면적", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/reports?view=forecast`) },
      { id: "go-schedule", group: "Task", label: "일정 탭", keywords: "schedule gantt 간트", when: () => !!pid(), run: () => appGo(`/projects/${pid()}/schedule`) },

      { id: "zoom-day", group: "보기", label: "줌: 일", shortcut: `${modKey}1`, run: () => s().setZoomPreset("day") },
      { id: "zoom-week", group: "보기", label: "줌: 주", shortcut: `${modKey}2`, run: () => s().setZoomPreset("week") },
      { id: "zoom-month", group: "보기", label: "줌: 월", shortcut: `${modKey}3`, run: () => s().setZoomPreset("month") },
      { id: "zoom-in", group: "보기", label: "확대", shortcut: `${modKey}=`, run: () => s().nudgeZoom(1) },
      { id: "zoom-out", group: "보기", label: "축소", shortcut: `${modKey}-`, run: () => s().nudgeZoom(-1) },
      { id: "fit", group: "보기", label: "플랜 맞춤", shortcut: `${modKey}0`, run: () => s().requestFit("project") },
      { id: "fit-sel", group: "보기", label: "선택 맞춤", when: () => !!focus(), run: () => s().requestFit("selection") },
      { id: "t-critical", group: "보기", label: "여유 없는 작업 강조 켜기/끄기", run: () => s().setUI({ showCritical: !s().ui.showCritical }) },
      { id: "t-deps", group: "보기", label: "작업 사이 연결선 켜기/끄기", run: () => s().setUI({ showDependencies: !s().ui.showDependencies }) },
      { id: "t-path", group: "보기", label: "선택 작업의 날짜를 정한 경로 강조 켜기/끄기", run: () => s().setUI({ highlightDrivingPath: !s().ui.highlightDrivingPath }) },
      { id: "t-baseline", group: "보기", label: "처음 계획(기준선) 표시 켜기/끄기", run: () => s().setUI({ showBaseline: !s().ui.showBaseline }) },
      { id: "t-progress", group: "보기", label: "진척선 켜기/끄기", run: () => s().setUI({ showProgressLine: !s().ui.showProgressLine }) },
      { id: "t-float", group: "보기", label: "여유 표시 켜기/끄기", run: () => s().setUI({ showFloat: !s().ui.showFloat }) },
      { id: "t-progress-label", group: "보기", label: "막대 안 진척률 켜기/끄기", run: () => s().setUI({ showProgressLabel: !s().ui.showProgressLabel }) },
      { id: "t-remaining-label", group: "보기", label: "단계 남은 기간 켜기/끄기", run: () => s().setUI({ showRemainingLabel: !s().ui.showRemainingLabel }) },
      { id: "collapse", group: "보기", label: "모두 접기", run: () => s().setCollapsedAll(true) },
      { id: "expand", group: "보기", label: "모두 펼치기", run: () => s().setCollapsedAll(false) },
      { id: "nav", group: "보기", label: "탐색 사이드바 접기/펼치기", shortcut: `${modKey}B`, run: () => useCatalogStore.getState().setNavCollapsed(!useCatalogStore.getState().navCollapsed) },
      { id: "panel", group: "보기", label: "사이드 패널 열기/닫기", shortcut: `${modKey}I`, run: () => s().setUI({ sidePanel: s().ui.sidePanel ? null : s().focusId ? "inspector" : "insights" }) },
      { id: "insights", group: "보기", label: "인사이트 패널", run: () => s().setUI({ sidePanel: "insights" }) },
      { id: "inspector", group: "보기", label: "인스펙터 패널", when: () => !!focus(), run: () => s().setUI({ sidePanel: "inspector" }) },
      { id: "theme", group: "보기", label: "화면 모드: 라이트/다크 전환", run: () => s().setUI({ theme: document.documentElement.classList.contains("dark") ? "light" : "dark" }) },

      { id: "today", group: "이동", label: "오늘로 이동", shortcut: `${modKey}T`, when: editing, run: () => s().scrollToDate(todayISO()) },
      { id: "start", group: "이동", label: "플랜 시작으로 이동", when: editing, run: () => s().scrollToDate(s().project.start) },
      { id: "finish", group: "이동", label: "플랜 종료로 이동", when: editing, run: () => s().scrollToDate(s().derived.schedule.projectFinish) },
      { id: "status-date", group: "이동", label: "진척 기준일로 이동", when: editing, run: () => s().scrollToDate(s().project.statusDate) },
      {
        id: "first-critical",
        group: "이동",
        label: "여유 없는 첫 작업 선택",
        run: () => {
          const id = s().derived.schedule.criticalPath.find((x) => !s().derived.schedule.tasks.get(x)?.isSummary);
          if (id) s().select([id], id);
        },
      },
    );
    return items;
  }, [openDialog, s, surface, workspaceRole]);

  const workspaceDocs = useWorkspaceDocRows();
  const activeId = useProjectStore((st) => st.activeId);
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const q = query.trim();
  const quick = q ? parseQuickEdit(q) : null;
  const dateJump = q && !quick ? parseDate(q) : null;

  const taskHits = useMemo(() => {
    if (!q || quick) return [];
    const out: Array<{ id: string; name: string; wbs: string; start: string; critical: boolean; milestone: boolean }> = [];
    for (const id of schedule.order) {
      const t = schedule.tasks.get(id)!;
      const task = project.tasks.find((x) => x.id === id)!;
      if (tokensMatch(q, `${t.wbs} ${task.name} ${task.assignee ?? ""}`)) out.push({ id, name: task.name, wbs: t.wbs, start: t.start, critical: t.critical, milestone: t.isMilestone });
      if (out.length >= 12) break;
    }
    return out;
  }, [q, quick, schedule, project]);

  const executionHits = useMemo(() => {
    if (!q || quick) return [];
    const out: Array<{ id: string; title: string; status: ExecutionStatus | undefined; due?: string; assignees: string }> = [];
    for (const t of project.executionTasks) {
      const bucket = project.planner.buckets.find((b) => b.id === t.bucketId)?.name ?? "";
      if (tokensMatch(q, `${t.title} ${t.assignees.join(" ")} ${bucket} ${t.notes ?? ""}`)) {
        out.push({ id: t.id, title: t.title, status: t.status, due: t.due, assignees: t.assignees.join(", ") });
      }
      if (out.length >= 8) break;
    }
    return out;
  }, [q, quick, project]);

  const docHits = useMemo(() => {
    if (quick) return [];
    return paletteWorkspaceDocuments(
      workspaceDocs.filter((row) => row.contentReadable),
      q,
    );
  }, [q, quick, workspaceDocs]);

  const fileHits = useMemo(() => {
    if (!q || quick) return [];
    const projects = Object.values(catalogProjects).map((item) => (activeId && item.id === activeId ? project : item));
    const out = [];
    for (const entry of collectFileEntries(projects)) {
      if (tokensMatch(q, `${entry.attachment.title} ${entry.ownerTitle} ${entry.ownerCode ?? ""} ${entry.projectName}`)) out.push(entry);
      if (out.length >= 8) break;
    }
    return out;
  }, [q, quick, catalogProjects, activeId, project]);

  const computedPlans = useMemo(() => {
    if (!open) return null;
    const today = todayISO();
    const rows = Object.values(catalogProjects)
      .filter((item) => (item.lifecycle ?? "active") !== "trashed")
      .map((item) => {
        const source = activeId && item.id === activeId ? project : item;
        const snap = inspectPlan(source, today);
        const cover = planCover(snap, today);
        const tone: DotTone = snap.lifecycle === "archived" ? "muted" : snap.tone === "late" ? "late" : snap.tone === "done" ? "done" : snap.tone === "empty" ? "muted" : "active";
        return {
          id: item.id,
          name: source.name?.trim() || "이름 없는 플랜",
          detail: cover.detail,
          status: cover.status,
          tone,
          updatedAt: source.updatedAt ?? "",
        };
      });
    rows.sort((a, b) => {
      if (a.id === activeId) return -1;
      if (b.id === activeId) return 1;
      if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
      return a.name.localeCompare(b.name, "ko");
    });
    return rows;
  }, [open, catalogProjects, activeId, project]);
  const planCache = useRef(computedPlans ?? []);
  if (computedPlans) planCache.current = computedPlans;
  const planRows = computedPlans ?? planCache.current;

  const planHits = useMemo(() => {
    if (quick) return [];
    const matched = q ? planRows.filter((row) => tokensMatch(q, `${row.name} ${row.status} ${row.detail} 플랜 열기`)) : planRows;
    return matched.slice(0, q ? 8 : 20);
  }, [planRows, q, quick]);

  const cmdHits = commands.filter((c) => (c.when ? c.when() : true) && (!q || tokensMatch(q, `${c.label} ${c.detail ?? ""} ${c.keywords ?? ""} ${c.group}`)));
  const counts = {
    plan: planHits.length,
    task: taskHits.length,
    execution: executionHits.length,
    doc: docHits.length,
    file: fileHits.length,
    move: cmdHits.filter((c) => c.group === "이동").length + (dateJump && dateJump !== "clear" ? 1 : 0),
    command: cmdHits.filter((c) => c.group !== "이동").length + (quick ? 1 : 0),
  };
  const total = counts.plan + counts.task + counts.execution + counts.doc + counts.file + counts.move + counts.command;
  const scopeCount = scope === "all" ? total : counts[scope];
  const activeScope: PaletteScope = scope !== "all" && scopeCount === 0 ? "all" : scope;
  useEffect(() => {
    if (!open || scope === "all" || scopeCount > 0) return;
    setScope("all");
  }, [open, scope, scopeCount]);

  const show = (id: PaletteScope) => activeScope === "all" || activeScope === id;
  const sections = q ? (["quick", "plan", "task", "execution", "doc", "file", "move", "command", "taskCmd", "view"] as const) : (["plan", "doc", "move", "command", "taskCmd", "view"] as const);

  function commandSection(group: "이동" | "명령" | "Task" | "보기") {
    const items = cmdHits.filter((c) => c.group === group);
    if (!items.length) return null;
    return (
      <Section key={group} heading={group}>
        {items.map((c) => (
          <CommandItem key={c.id} value={c.id} onSelect={() => run(c.run)} className={itemClass}>
            <Hit icon={iconFor(c)} title={c.label} detail={c.detail} meta={c.shortcut ? <span className="shrink-0 text-caption text-muted-foreground">{c.shortcut}</span> : undefined} />
          </CommandItem>
        ))}
      </Section>
    );
  }

  function renderSection(id: (typeof sections)[number]) {
    if (id === "quick" && quick && show("command")) {
      return (
        <Section key="quick" heading="빠른 입력">
          <CommandItem value="quick" disabled={!!quick.error} onSelect={() => run(quick.apply)} className={itemClass}>
            <Hit
              icon={<Sparkle16Regular className={quick.error ? "text-muted-foreground" : "text-tint"} />}
              title={quick.label}
              detail={quick.hint}
              detailClassName={quick.error || (quick.impact != null && quick.impact.finishDeltaDays > 0) ? "text-critical" : undefined}
              wrapDetail
              meta={quick.error ? undefined : <span className="shrink-0 text-caption text-muted-foreground">적용</span>}
            />
          </CommandItem>
        </Section>
      );
    }
    if (id === "plan" && show("plan") && planHits.length > 0) {
      return (
        <Section key="plan" heading="플랜">
          {planHits.map((row) => (
            <CommandItem key={row.id} value={`plan-${row.id}`} onSelect={() => run(() => appGo(`/projects/${row.id}/schedule`))} className={itemClass}>
              <Hit icon={<Board16Regular />} title={row.name} detail={row.detail} meta={<DotLabel tone={row.tone} label={row.status} />} />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "task" && show("task") && taskHits.length > 0) {
      return (
        <Section key="task" heading="작업">
          {taskHits.map((t) => (
            <CommandItem
              key={t.id}
              value={`task-${t.id}`}
              onSelect={() =>
                run(() => {
                  s().select([t.id], t.id);
                  s().scrollToDate(t.start);
                })
              }
              className={itemClass}
            >
              <Hit
                icon={t.milestone ? <Diamond16Regular /> : <ArrowRight16Regular />}
                title={t.name || "이름 없는 작업"}
                detail={[t.wbs, t.milestone ? "마일스톤" : null, formatDate(t.start, "short")].filter(Boolean).join(" · ")}
                meta={t.critical ? <DotLabel tone="late" label="여유 없음" /> : undefined}
              />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "execution" && show("execution") && executionHits.length > 0) {
      return (
        <Section key="execution" heading="Task">
          {executionHits.map((t) => (
            <CommandItem key={t.id} value={`x-${t.id}`} onSelect={() => run(() => s().setExecutionFocus(t.id))} className={itemClass}>
              <Hit
                icon={<CheckboxChecked16Regular />}
                title={t.title || "제목 없음"}
                detail={[t.assignees || "담당 없음", t.due ? formatDate(t.due, "short") : null].filter(Boolean).join(" · ")}
                meta={<ExecutionMeta status={t.status} />}
              />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "doc" && show("doc") && docHits.length > 0) {
      return (
        <Section key="doc" heading="문서">
          {docHits.map((row) => (
            <CommandItem key={row.key} value={`doc-${row.key}`} onSelect={() => run(() => appGo(planHref(row.projectId, `docs/${row.doc.id}`)))} className={itemClass}>
              <Hit icon={<Document16Regular />} title={documentTitle(row.doc.title)} detail={[documentCode(row.doc.seq), DOCUMENT_KIND_LABEL[row.doc.kind], row.projectName].join(" · ")} />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "file" && show("file") && fileHits.length > 0) {
      return (
        <Section key="file" heading="파일">
          {fileHits.map((entry) => (
            <CommandItem key={entry.key} value={`file-${entry.key}`} onSelect={() => run(() => appGo(fileOpenPath(entry)))} className={itemClass}>
              <Hit icon={fileGlyph(entry.kind)} title={entry.attachment.title || "이름 없는 파일"} detail={[entry.projectName, fileSourceCaption(entry), fileSourceTitle(entry)].filter(Boolean).join(" · ")} />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "move" && show("move")) {
      const items = cmdHits.filter((c) => c.group === "이동");
      const jump = !!(dateJump && dateJump !== "clear");
      if (!items.length && !jump) return null;
      return (
        <Section key="move" heading="이동">
          {jump && dateJump && dateJump !== "clear" && (
            <CommandItem value={`jump-${dateJump}`} onSelect={() => run(() => s().scrollToDate(dateJump))} className={itemClass}>
              <Hit icon={<CalendarLtr16Regular />} title={formatDate(dateJump, "full")} detail="이 날짜로 이동합니다" />
            </CommandItem>
          )}
          {items.map((c) => (
            <CommandItem key={c.id} value={c.id} onSelect={() => run(c.run)} className={itemClass}>
              <Hit icon={iconFor(c)} title={c.label} detail={c.detail} meta={c.shortcut ? <span className="shrink-0 text-caption text-muted-foreground">{c.shortcut}</span> : undefined} />
            </CommandItem>
          ))}
        </Section>
      );
    }
    if (id === "command" && show("command")) return commandSection("명령");
    if (id === "taskCmd" && show("command")) return commandSection("Task");
    if (id === "view" && show("command")) return commandSection("보기");
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogHeader className="sr-only">
        <DialogTitle>검색</DialogTitle>
        <DialogDescription>플랜, 작업, 명령을 찾습니다</DialogDescription>
      </DialogHeader>
      <DialogContent className="top-[12%] translate-y-0 overflow-hidden p-0 sm:max-w-[480px]" showCloseButton={false}>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-normal [&_[cmdk-item][data-selected=true]_.palette-go]:opacity-100">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="플랜, 작업, 명령 검색"
            trailing={
              <button type="button" className="shrink-0 rounded-md border border-border bg-foreground/[0.04] px-1.5 py-0.5 text-caption text-muted-foreground hover:bg-foreground/[0.08]" onClick={close}>
                Esc
              </button>
            }
          />
          {total > 0 && (
            <div className="flex gap-1 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-label="검색 범위" onMouseDown={(e) => e.preventDefault()}>
              {SCOPE_CHIPS.map((chip) => {
                const count = chip.id === "all" ? total : counts[chip.id];
                if (chip.id !== "all" && count === 0) return null;
                const on = activeScope === chip.id;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    aria-pressed={on}
                    className={cn(
                      "inline-flex h-6 shrink-0 items-center gap-1 rounded-full border px-2.5 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring",
                      on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-foreground/5 hover:text-foreground",
                    )}
                    onClick={() => setScope(chip.id)}
                  >
                    {chip.label}
                    <span className={cn("tabular", on ? "text-primary-foreground/80" : "text-muted-foreground")}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
          <CommandList className="max-h-[420px]">
            <CommandEmpty>{activeScope === "all" ? "일치하는 항목이 없습니다." : "이 범위에는 없습니다."}</CommandEmpty>
            {sections.map((id) => renderSection(id))}
          </CommandList>
          <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-caption text-muted-foreground">
            <span className="shrink-0">↑↓ 이동</span>
            <span className="shrink-0">↩ 열기</span>
            {!q && (
              <span className="ml-auto min-w-0 truncate">
                빠른 입력 <span className="tabular">3.2 기간 8</span> · <span className="tabular">3.2 진척 50</span>
              </span>
            )}
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

